/* istanbul ignore file */
/* tslint:disable */
import * as codec from 'purify-ts/Codec';
import { array as codecArray, oneOf as codecOneOf, string as codecString } from 'purify-ts/Codec';
import { utils as coreUtils, requestParams } from '@codegrade/api-core';
import { CourseCouponUsage, ExtendedTransaction, ExtendedUser, LoginUserData, LogoutResponse, LogoutUserData, PatchUserData, RegisterUserData, ResultDataPostUserLogin, ResultDataPostUserRegister, SessionRestrictionData, TenantCouponUsage, User, UserLoginResponse, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
import { makeResponses } from '../response_factory';
const makeResult = coreUtils.makeResult;
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function user(config) {
    const patchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, /* #__PURE__ */ parseJsonResponse(ExtendedUser)),
    });
    const getCouponUsagesResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecArray(
        /* #__PURE__ */ parsers.taggedUnion('scope', [
            ['course', CourseCouponUsage],
            ['tenant', TenantCouponUsage],
        ])))),
    });
    const getTransactionsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(ExtendedTransaction))),
    });
    const loginResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(ResultDataPostUserLogin)),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecOneOf([
            ExtendedUser,
            User,
            /* #__PURE__ */ codec.record(codecString, codecString),
        ]))),
    });
    const registerResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(ResultDataPostUserRegister)),
    });
    const restrictResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(UserLoginResponse)),
    });
    const logoutResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, /* #__PURE__ */ parseJsonResponse(LogoutResponse)),
    });
    const searchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(config, 
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(User))),
    });
    return Object.freeze({
        /**
         * Update the attributes of a user.
         *
         * @param arg The arguments to the function.
         * @param arg.userId The id of the user you want to change. Currently this can only be your own user id.
         * @param arg.requestBody The body of the request.
         * @returns The updated user.
         */
        patch: async ({ userId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/users/${userId}`, patchResponse.v, JSON.stringify(PatchUserData.encode(requestBody)));
            return result;
        },
        /**
         * Get all the coupons used for the specified user.
         *
         * @param arg The arguments to the function.
         * @param arg.userId The user to get the coupons used for.
         * @returns All coupons used for the given user.
         */
        getCouponUsages: async ({ userId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/users/${userId}/coupon_usages/`, getCouponUsagesResponse.v, undefined);
            return result;
        },
        /**
         * Get all transactions for the specified user.
         *
         * @param arg The arguments to the function.
         * @param arg.userId The user to get the transactions for.
         * @returns All transactions for the given user.
         */
        getTransactions: async ({ userId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/users/${userId}/transactions/`, getTransactionsResponse.v, undefined);
            return result;
        },
        /**
         * Login using your username and password.
         *
         * `permissions` to the user. The value will be a mapping indicating which global permissions this user has.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @param arg.withPermissions Return the global permissions of the newly logged in user.
         * @returns A response containing the JSON serialized user
         */
        login: async ({ requestBody, withPermissions = undefined, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/login`, loginResponse.v, JSON.stringify(LoginUserData.encode(requestBody)), {
                with_permissions: withPermissions,
            });
            return result;
        },
        /**
         * Get the info of the currently logged in user.
         *
         * @param arg The arguments to the function.
         * @param arg.type If this is `roles` a mapping between course_id and role name will be returned, if this is `extended` an `ExtendedUser` instead of a `User` will be returned.
         * @param arg.extended If `true` this has the same effect as setting `type` to `extended`.
         * @param arg.withPermissions Setting this to true will add the key `permissions` to the user. The value will be a mapping indicating which global permissions this user has.
         * @returns A response containing the JSON serialized user
         */
        get: async ({ type = undefined, extended = undefined, withPermissions = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/login`, getResponse.v, undefined, {
                type,
                extended,
                with_permissions: withPermissions,
            });
            return result;
        },
        /**
         * Create a new user.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns The registered user and an `access_token` that can be used to perform requests as this new user.
         */
        register: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/user`, registerResponse.v, JSON.stringify(RegisterUserData.encode(requestBody)));
            return result;
        },
        /**
         * Revoke a given token.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns An new token that has the given restrictions added.
         */
        restrict: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/token/restrict`, restrictResponse.v, JSON.stringify(SessionRestrictionData.encode(requestBody)));
            return result;
        },
        /**
         * Revoke a given token.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns An empty 200 response.
         */
        logout: async ({ requestBody, }) => {
            const body = coreUtils.toFormData(LogoutUserData.encode(requestBody));
            const result = await requestParams(config, 'POST', `/api/v1/token/revoke`, logoutResponse.v, body);
            return result;
        },
        /**
         * Search for a user by name and username.
         *
         * @param arg The arguments to the function.
         * @param arg.q The string to search for, all SQL wildcard are escaped and spaces are replaced by wildcards.
         * @param arg.excludeCourse Exclude all users that are in the given course from the search results. You need the permission `can_list_course_users` on this course to use this parameter.
         * @returns The users that match the given query string.
         */
        search: async ({ q, excludeCourse = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/users/`, searchResponse.v, undefined, {
                q,
                exclude_course: excludeCourse,
            });
            return result;
        },
    });
}
